import "./PasswordPage.scss";
import "./register/RegisterPage.scss";
import React, {useEffect, useState} from "react";
import MembersApi from "../api/MembersApi";
import AppApi from "../api/AppApi";
import LoginPageStep from "./register/LoginPageStep";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {LogRegLogo} from "./util/LogRegLogo";
import RegisterPageLeft from "./register/RegisterPageLeft";
import {RegStep} from "./register/RegisterPage";
import {useMicrosoftAuth} from "./login/MicrosoftAuthButton";
import {ReactComponent as MicrosoftIcon} from "./register/icons/microsoft.svg";
import {Spinner} from "./account/misc/Spinner";
import {SsoMember} from "../generated/dashboard-client";
import Storage from "../util/Storage";
import {env} from "../env/env";

export default function PasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sso, setSso] = useState(false);
  const [msProcessing, setMsProcessing] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  // MSAL runs with navigateToLoginRequestUrl=true (the default), so after "Continue with Microsoft"
  // the redirect is consumed on /account/login and the browser is navigated BACK to this page (the
  // login-request URL). That means we must finish the sign-in HERE: getMicrosoftMember has already
  // resolved the SSO account, so log into MHM with its credential and go to the dashboard. This was
  // previously a no-op, which left users authenticated with Microsoft but stranded on the forgot-
  // password page.
  const {loginWithMicrosoft} = useMicrosoftAuth({
    onStartLoading: () => setMsProcessing(true),
    onSuccess: async (member: SsoMember) => {
      const password = (member.token || member.ssoId || "").trim();
      if (!member.email || !password) {
        setMsProcessing(false);
        setSso(true);
        setError("We couldn't complete your Microsoft sign-in. Please try again or contact your administrator.");
        return;
      }
      try {
        const loginResp = await AppApi.login({username: member.email, password});
        if (loginResp.data.token) {
          Storage.setToken(loginResp.data.token);
          navigate(env.dashboardPath);
        } else {
          setMsProcessing(false);
          setSso(true);
          setError("We couldn't complete your Microsoft sign-in. Please try again or contact your administrator.");
        }
      } catch (e) {
        setMsProcessing(false);
        setSso(true);
        setError("We couldn't complete your Microsoft sign-in. Please try again or contact your administrator.");
      }
    },
  });

  useEffect(() => {
    if (!state.email) return;
    setEmail(state.email);
  }, [state.email]);

  async function submit() {
    setError('');
    try {
      setLoading(true);
      await MembersApi.generatePassword(email);
      setSuccess(true);
    } catch (err: any) {
      setLoading(false);
      const message = err?.response?.data?.message || err?.response?.data || '';
      if (message === 'sso_microsoft') {
        // SSO account — can't reset a password. Show the dedicated single sign-on screen.
        setSso(true);
      } else {
        setError('The email address is not associated with an account. Please try another email address.');
      }
    }
  }

  function onBack() {
    window.history.back();
  }

  // Completing the Microsoft redirect (logging into MHM): show a spinner instead of flashing the
  // forgot-password form while we finish signing the user in.
  if (msProcessing) {
    return (
      <div className={"reg-page"}>
        <div className={"left"}>
          <RegisterPageLeft step={RegStep.Login} />
        </div>
        <div className={"right"}>
          <div className={"step-type step full"}>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  if (sso) {
    return (
      <div className={"reg-page"}>
        <div className={"left"}>
          <RegisterPageLeft step={RegStep.Login} />
        </div>
        <div className={"right"}>
          <div className={"step-type step full"}>
            <LogRegLogo />
            <h2>This account uses Single Sign-On (Microsoft Azure AD).</h2>
            <h5>Please sign in with Microsoft or contact your IT administrator.</h5>
            {error && <h6 className={"err"}>{error}</h6>}
            <div className={"login-buttons"}>
              <div onClick={() => loginWithMicrosoft()}>
                <MicrosoftIcon />
                <span className={"login-option"}>Continue with Microsoft</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={"reg-page"}>
      <div className={'left'}>
        <div className="cvr"/>
      </div>
      <div className={'right'}>
        {!success && <LoginPageStep email={email} step={'forgot-password'} onEmailChange={setEmail} onBack={onBack} loading={loading} error={error} onNext={submit} password={''} onPasswordChange={() => {}}/>}
        {success && <div className={"success-next step full"}>
          <img src={"https://www.musthavemenus.com/imageservice/images/img/13/icons/check.svg"} alt={"check"} />
          <h2>Password Sent</h2>
          <h5>
            Email has been sent to {email} containing a new password.
          </h5>
          <footer>
            <NavLink className={"ant-btn ant-btn-default ant-btn-lg primary pass-res"} to={"/account/login"} state={{email, step: 'pass'}}>Sign in to reset</NavLink>
          </footer>
        </div>}
      </div>
    </div>
  );
}
